import apiClient from './client';

export type JourneyEndReason = 'quit' | 'fired' | 'retired' | 'moved_on' | 'other';

const JOURNEY_END_REASON_LABELS: Record<JourneyEndReason, string> = {
  quit: 'Quit',
  fired: 'Fired',
  retired: 'Retired',
  moved_on: 'Moved on',
  other: 'Other',
};

export function formatJourneyEndReasonLabel(reason: string | null | undefined): string {
  if (reason == null || reason === '') return '—';
  if (reason in JOURNEY_END_REASON_LABELS) {
    return JOURNEY_END_REASON_LABELS[reason as JourneyEndReason];
  }
  return reason.replace(/_/g, ' ');
}

export type PlanterContactType = 'phone' | 'text' | 'email' | 'in-person' | 'other';
export type LegacyPlanterContactType = PlanterContactType | 'video' | 'hub-checkin';

export interface PlanterAppInviteSummary {
  expiresAt: string;
  usedAt: string | null;
  createdAt: string;
}

export interface Planter {
  id: number;
  userId?: number | null;
  /** True when user_id points at a user with the church_planter role (real app link). */
  hasPlanterAppUserLinked?: boolean;
  name: string;
  spouseName?: string | null;
  email?: string | null;
  /** Legacy client-only; prefer `formatPlanterLocation` + city/state from API */
  location?: string;
  phone?: string | null;
  secondaryPhone?: string | null;
  city?: string | null;
  state?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  address?: string;
  mailingAddressStreet?: string | null;
  mailingAddressStreet2?: string | null;
  mailingAddressCity?: string | null;
  mailingAddressState?: string | null;
  mailingAddressZip?: string | null;
  mailingAddressCountry?: string | null;
  churchName?: string | null;
  supportingChurch?: string;
  status: 'prospect' | 'candidate' | 'active' | 'graduated' | 'inactive' | 'withdrawn';
  stage?: 'inquiry' | 'application' | 'assessment' | 'training' | 'planting' | 'established' | null;
  pathKind?: 'growth' | 'planting' | null;
  plantingStep?: 'training' | 'funding' | 'post_funding' | null;
  /** Server-computed when funding end date has passed */
  effectivePlantingStep?: 'training' | 'funding' | 'post_funding' | null;
  trainingCompletedAt?: string | null;
  fundingEndDate?: string | null;
  journeyEndedAt?: string | null;
  journeyEndReason?: JourneyEndReason | string | null;
  journeyEndNotes?: string | null;
  contactInfo?: any;
  demographics?: any;
  education?: any;
  ministry?: any;
  family?: any;
  notes?: string;
  headshotImagePath?: string | null;
  familyPhotoImagePath?: string | null;
  assignedCoach?: number | null;
  assignedHub?: string;
  assignedHubLeader?: number;
  startDate?: string;
  graduationDate?: string;
  lastContactedAt?: string;
  nextFollowUpDate?: string;
  user?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  };
  coach?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
  hubLeader?: {
    id: number;
    firstName: string;
    lastName: string;
    email?: string;
  } | null;
  createdAt: string;
  updatedAt: string;
  /** Latest app invite metadata (no token); only when loaded from single-planter GET */
  appInvite?: PlanterAppInviteSummary | null;
}

/** City + state from the church planter row (API has no single `location` column). */
export function formatPlanterLocation(planter: Pick<Planter, 'city' | 'state'>): string {
  const parts = [planter.city, planter.state].filter(Boolean);
  return parts.length ? parts.join(', ') : '';
}

export interface PlanterContact {
  id: number;
  planterId: number;
  contactDate: string;
  contactType: LegacyPlanterContactType;
  notes: string;
  followUpDate?: string;
  followUpCompleted: boolean;
  contactedBy: number;
  createdAt: string;
  contactedByUser?: {
    id: number;
    firstName: string;
    lastName: string;
  };
}

export type ContactStatusValue =
  | 'contacted'
  | 'pending'
  | 'overdue'
  | 'needs_in_person'
  | 'exempt';

/** Prefer `contactStatusDetail` when present (full semantics); `contactStatus` is legacy-safe for old iOS. */
export function effectiveHubContactStatus(row: {
  contactStatus: ContactStatusValue;
  contactStatusDetail?: ContactStatusValue;
}): ContactStatusValue {
  return row.contactStatusDetail ?? row.contactStatus;
}

export interface PlanterContactCadenceInfo {
  touchFrequencyDays: number;
  phaseLabel: string;
  inPersonRequired: boolean;
}

export interface PlanterWithContactStatus extends Planter {
  /** Legacy-safe for old mobile clients (`contacted` | `pending` | `overdue` only). */
  contactStatus: ContactStatusValue;
  /** Full hub-contact semantics when different from legacy `contactStatus`. */
  contactStatusDetail?: ContactStatusValue;
  lastContact: {
    contactDate: string;
    notes?: string;
    contactType: string;
  } | null;
  touchStatus?: 'contacted' | 'pending' | 'overdue';
  inPersonRequired?: boolean;
  inPersonStatus?: 'contacted' | 'pending' | 'overdue';
  lastInPersonContact?: { contactDate: string; contactType: string } | null;
  contactCadence?: PlanterContactCadenceInfo;
}

export interface HubContactStatusResponse {
  success: boolean;
  data: PlanterWithContactStatus[];
  cadenceInfo: string;
  /** @deprecated Mobile legacy; use `cadenceInfo` */
  frequencyDays: number;
  periodStart: string;
  periodEnd: string;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface PlantersQueryParams {
  status?: string;
  stage?: string;
  pathKind?: string;
  plantingStep?: string;
  /** Matches server-side effective step (funding vs post-funding by date) */
  effectivePlantingStep?: 'funding' | 'post_funding';
  pathKindUnset?: 'true';
  journeyEnded?: 'true' | 'false';
  journeyEndReason?: string;
  assignedCoach?: string;
  assignedHub?: string;
  assignedHubLeader?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface PipelineProfileDto {
  kind: 'church_planter' | 'portal_candidate';
  id: number;
  name: string;
  churchName: string | null;
  location: string | null;
  headshotImagePath: string | null;
  /** Present for church_planter rows when pipeline profiles include user link info */
  hasPlanterAppUserLinked?: boolean;
}

export interface PipelineOverviewPortalBucket {
  key: string;
  label: string;
  count: number;
}

export interface PipelineNetworkMetrics {
  planterContacts: {
    totalLoggedContacts: number;
    plantersWithLoggedContact: number;
  };
  mbcb: {
    reportRowsIncluded: number;
    /** Sum of each plant’s average Sunday attendance (up to 2 reports: current + previous month). */
    sumPlantAvgSundayAttendance: number;
    sumBudgetReceiptsMonthly: number;
    sumMissionGiving: number;
    sumCooperativeProgramGiving: number;
    sumBaptisms: number;
    sumSalvations: number;
    statusesIncluded: string[];
  };
  churchPlanters: {
    activeJourney: number;
  };
}

export interface PlanterPipelineOverviewData {
  portal: {
    prePlanterTotal: number;
    buckets: PipelineOverviewPortalBucket[];
  };
  planters: {
    activeTotal: number;
    active: {
      growth: number;
      training: number;
      funding: number;
      postFunding: number;
      needsPath: number;
    };
    ended: {
      total: number;
      quit: number;
      fired: number;
      retired: number;
      movedOn: number;
      other: number;
    };
  };
  networkMetrics: PipelineNetworkMetrics;
  profiles?: {
    portal: Record<string, PipelineProfileDto[]>;
    planters: {
      growth: PipelineProfileDto[];
      training: PipelineProfileDto[];
      funding: PipelineProfileDto[];
      postFunding: PipelineProfileDto[];
      needsPath: PipelineProfileDto[];
    };
  };
}

export const getPlanterPipelineOverview = async (params?: {
  assignedHub?: string;
  includeProfiles?: boolean;
}): Promise<{ success: boolean; data: PlanterPipelineOverviewData }> => {
  const { includeProfiles, ...rest } = params || {};
  const query = {
    ...rest,
    ...(includeProfiles ? { includeProfiles: 'true' as const } : {}),
  };
  const response = await apiClient.get('/planters/pipeline-overview', { params: query });
  return response.data;
};

export const getPlanters = async (params?: PlantersQueryParams): Promise<PaginatedResponse<Planter[]>> => {
  const response = await apiClient.get('/planters', { params });
  return response.data;
};

/** Matches backend `PLANTING_STEP_SCOPE` for draft generation */
export const MBCB_PLANTING_STEP_SCOPE = {
  FUNDING: 'funding',
  FUNDING_OR_POST_FUNDING: 'funding_or_post_funding',
} as const;

export type MbcbPlantingStepScope =
  (typeof MBCB_PLANTING_STEP_SCOPE)[keyof typeof MBCB_PLANTING_STEP_SCOPE];

export interface RequestMbcbDraftsResponseData {
  createdCount: number;
  skippedCount: number;
  createdReportIds: number[];
  period: { month: number; year: number };
  mode: 'single' | 'bulk';
  plantingStepScope?: MbcbPlantingStepScope;
}

/**
 * All church planters in effective funding stage (paginates until complete).
 * Excludes training, post-funding, and expired funding_end_date.
 */
export const getFundingPlantersRawStep = async (): Promise<Planter[]> => {
  const all: Planter[] = [];
  let page = 1;
  const limit = 100;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const res = await getPlanters({
      effectivePlantingStep: 'funding',
      journeyEnded: 'false',
      page,
      limit,
    });
    all.push(...res.data);
    if (page >= res.pagination.pages) break;
    page += 1;
  }
  return all;
};

export const requestMbcbDraftsForPeriod = async (params: {
  reportMonth: number;
  reportYear: number;
  plantingStepScope?: MbcbPlantingStepScope;
  planterId?: number | null;
}): Promise<RequestMbcbDraftsResponseData> => {
  const response = await apiClient.post<{ success: boolean; data: RequestMbcbDraftsResponseData }>(
    '/mbcb/reports/request',
    params,
  );
  return response.data.data;
};

export const getPlanter = async (id: number): Promise<Planter> => {
  const response = await apiClient.get(`/planters/${id}`);
  return response.data.data;
};

export const sendPlanterAppInvite = async (
  id: number,
): Promise<{ expiresAt: string; email: string }> => {
  const response = await apiClient.post(`/planters/${id}/app-invite`);
  return response.data.data;
};

/** Pipeline buckets that can be emailed as a group. */
export type PipelineEmailBucket = 'growth' | 'training' | 'funding' | 'post_funding';

export interface PipelineEmailRecipient {
  id: number;
  name: string;
  churchName: string | null;
  email: string;
}

export interface PipelineEmailRecipientsData {
  bucket: PipelineEmailBucket;
  label: string;
  recipients: PipelineEmailRecipient[];
  withoutEmail: { id: number; name: string; churchName: string | null }[];
  total: number;
  withEmailCount: number;
  withoutEmailCount: number;
}

export const getPipelineEmailRecipients = async (
  bucket: PipelineEmailBucket,
): Promise<PipelineEmailRecipientsData> => {
  const response = await apiClient.get('/planters/pipeline-email/recipients', {
    params: { bucket },
  });
  return response.data.data;
};

export interface PipelineEmailFailure {
  email: string;
  name: string | null;
  message: string;
}

export interface SendPipelineEmailResult {
  bucket: PipelineEmailBucket;
  sent: number;
  total: number;
  failed: number;
  failures: PipelineEmailFailure[];
}

export const sendPipelineEmail = async (params: {
  bucket: PipelineEmailBucket;
  subject: string;
  message: string;
  planterIds?: number[];
}): Promise<SendPipelineEmailResult> => {
  const response = await apiClient.post('/planters/pipeline-email', params);
  return response.data.data;
};

export interface PipelineEmailPreview {
  subject: string;
  html: string;
  text: string;
}

export const previewPipelineEmail = async (params: {
  subject: string;
  message: string;
  sampleName?: string;
}): Promise<PipelineEmailPreview> => {
  const response = await apiClient.post('/planters/pipeline-email/preview', params);
  return response.data.data;
};

export const getPlantersNeedingContact = async (): Promise<Planter[]> => {
  const response = await apiClient.get('/planters/needs-contact');
  return response.data.data;
};

export const createPlanter = async (data: Partial<Planter>): Promise<Planter> => {
  const response = await apiClient.post('/planters', data);
  return response.data.data;
};

export const updatePlanter = async (id: number, data: Partial<Planter>): Promise<Planter> => {
  const response = await apiClient.put(`/planters/${id}`, data);
  return response.data.data;
};

export const geocodePlanter = async (id: number, data: { street?: string; city?: string; state?: string; zip?: string; country?: string; }): Promise<Planter> => {
  const response = await apiClient.post(`/planters/${id}/geocode`, data);
  return response.data.data;
};

export const deletePlanter = async (id: number): Promise<void> => {
  await apiClient.delete(`/planters/${id}`);
};

export const uploadPlanterHeadshot = async (id: number, file: File) => {
  const formData = new FormData();
  formData.append('headshotImage', file);
  const response = await apiClient.post(`/planters/${id}/upload/headshot`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 30000,
  });
  return response.data.data as Planter;
};

export const uploadPlanterFamilyPhoto = async (id: number, file: File) => {
  const formData = new FormData();
  formData.append('familyPhotoImage', file);
  const response = await apiClient.post(`/planters/${id}/upload/family-photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 30000,
  });
  return response.data.data as Planter;
};

export const deletePlanterHeadshot = async (id: number) => {
  const response = await apiClient.delete(`/planters/${id}/headshot`);
  return response.data.data as Planter;
};

export const deletePlanterFamilyPhoto = async (id: number) => {
  const response = await apiClient.delete(`/planters/${id}/family-photo`);
  return response.data.data as Planter;
};

export const getPlanterContacts = async (planterId: number): Promise<PlanterContact[]> => {
  const response = await apiClient.get(`/planters/${planterId}/contacts`);
  return response.data.data;
};

export const getRecentPlanterContacts = async (planterId: number, limit: number = 5): Promise<PlanterContact[]> => {
  const response = await apiClient.get(`/planters/${planterId}/contacts/recent`, { 
    params: { limit } 
  });
  return response.data.data;
};

export const createContact = async (planterId: number, data: {
  contactDate: string;
  contactType: PlanterContactType;
  notes: string;
  followUpDate?: string;
}): Promise<PlanterContact> => {
  const { followUpDate, ...rest } = data;
  const body: Record<string, unknown> = { ...rest };
  if (followUpDate !== undefined && followUpDate !== '') {
    body.nextContactDate = followUpDate;
  }
  const response = await apiClient.post(`/planters/${planterId}/contacts`, body);
  return response.data.data;
};

export const updateContact = async (planterId: number, contactId: number, data: Partial<PlanterContact>): Promise<PlanterContact> => {
  const { followUpDate, ...rest } = data;
  const body: Record<string, unknown> = { ...rest };
  delete body.followUpCompleted;
  if (followUpDate !== undefined) {
    body.nextContactDate = followUpDate === '' ? null : followUpDate;
  }
  const response = await apiClient.put(`/planters/${planterId}/contacts/${contactId}`, body);
  return response.data.data;
};

export const deleteContact = async (planterId: number, contactId: number): Promise<void> => {
  await apiClient.delete(`/planters/${planterId}/contacts/${contactId}`);
};

export const getContactStats = async (planterId: number): Promise<any> => {
  const response = await apiClient.get(`/planters/${planterId}/contacts/stats`);
  return response.data.data;
};

export const getHubContactStatus = async (): Promise<HubContactStatusResponse> => {
  const response = await apiClient.get('/planters/hub-contact-status?include=userLink');
  return response.data;
};

export const promotePlanterFromGrowth = async (id: number, body?: { notes?: string }): Promise<Planter> => {
  const response = await apiClient.post(`/planters/${id}/actions/promote-to-planter`, body || {});
  return response.data.data;
};

export const completePlanterTraining = async (id: number, body?: { notes?: string }): Promise<Planter> => {
  const response = await apiClient.post(`/planters/${id}/actions/complete-training`, body || {});
  return response.data.data;
};

export const setPlanterFundingEnd = async (
  id: number,
  body: { fundingEndDate: string; notes?: string },
): Promise<Planter> => {
  const response = await apiClient.post(`/planters/${id}/actions/set-funding-end`, body);
  return response.data.data;
};

export const advancePlanterToPostFunding = async (id: number, body?: { notes?: string }): Promise<Planter> => {
  const response = await apiClient.post(`/planters/${id}/actions/advance-to-post-funding`, body || {});
  return response.data.data;
};

export const revertPlanterToFunding = async (id: number, body?: { notes?: string }): Promise<Planter> => {
  const response = await apiClient.post(`/planters/${id}/actions/revert-to-funding`, body || {});
  return response.data.data;
};

export const revertPlanterToTraining = async (id: number, body?: { notes?: string }): Promise<Planter> => {
  const response = await apiClient.post(`/planters/${id}/actions/revert-to-training`, body || {});
  return response.data.data;
};

export const endPlanterJourney = async (
  id: number,
  body: { reason: JourneyEndReason; notes?: string },
): Promise<Planter> => {
  const response = await apiClient.post(`/planters/${id}/actions/end-journey`, body);
  return response.data.data;
};

export interface PlanterMbcbReportSummary {
  id: number;
  planterId: number;
  reportMonth: number;
  reportYear: number;
  status: string;
  submittedAt?: string | null;
}

type SundayKey = 's1' | 's2' | 's3' | 's4' | 's5';

/** Per-Sunday metrics we collect in-app (legacy JSONB may contain other keys). */
export interface WeeklyMetricRow {
  worshipAttendance: Record<SundayKey, number>;
}

export interface GivingBreakdown {
  income: {
    congregationalOffering: number | null;
    sponsoringChurches: number | null;
    associations: number | null;
    mbcb: number | null;
    otherSupport: number | null;
    total: number | null;
  };
  missionGiving: {
    cooperativeProgram: number | null;
    associations: number | null;
    margaretLackey: number | null;
    otherMissionCauses: number | null;
    total: number | null;
  };
}

export interface MbcbReportDetail extends PlanterMbcbReportSummary {
  sundayAttendance?: number | null;
  smallGroupAttendance?: number | null;
  newMembers?: number | null;
  baptisms?: number | null;
  salvations?: number | null;
  monthlyIncome?: number | null;
  monthlyExpenses?: number | null;
  buildingFund?: number | null;
  missionsGiving?: number | null;
  ministryHighlights?: string | null;
  communityOutreach?: string | null;
  discipleshipActivities?: string | null;
  leadershipDevelopment?: string | null;
  currentChallenges?: string | null;
  prayerRequests?: string | null;
  supportNeeded?: string | null;
  shortTermGoals?: string | null;
  longTermVision?: string | null;
  nextMonthFocus?: string | null;
  volunteerHours?: number | null;
  communityPartnerships?: string | null;
  specialEvents?: string | null;
  additionalNotes?: string | null;
  attendanceNotes?: string | null;
  givingNotes?: string | null;
  weeklyMetrics?: Partial<WeeklyMetricRow>;
  givingBreakdown?: Partial<GivingBreakdown>;
  source?: string | null;
  externalId?: string | null;
  createdAt?: string;
  updatedAt?: string;
  submittedAt?: string | null;
  planter?: {
    id: number;
    name: string;
    spouseName?: string | null;
    churchName?: string | null;
    mbcbReportingEnabled?: boolean;
    assignedHubLeader?: number | null;
  };
}

export interface MbcbReportPayload {
  planterId?: number;
  reportMonth: number;
  reportYear: number;
  sundayAttendance?: number;
  smallGroupAttendance?: number;
  newMembers?: number;
  baptisms?: number;
  salvations?: number;
  monthlyIncome?: number;
  monthlyExpenses?: number;
  buildingFund?: number;
  missionsGiving?: number;
  ministryHighlights?: string | null;
  prayerRequests?: string | null;
  supportNeeded?: string | null;
  additionalNotes?: string | null;
  nextMonthFocus?: string | null;
  attendanceNotes?: string | null;
  givingNotes?: string | null;
  weeklyMetrics?: WeeklyMetricRow;
  givingBreakdown?: GivingBreakdown;
}

export interface MbcbDuplicateConflict {
  reportId: number;
  existingReportId: number;
  planterId: number;
  reportMonth: number;
  reportYear: number;
}

export const getPlanterMbcbReports = async (
  planterId: number,
): Promise<PlanterMbcbReportSummary[]> => {
  const response = await apiClient.get(`/planters/${planterId}/reports`);
  return response.data.data;
};

export const getMbcbReportDetail = async (reportId: number): Promise<MbcbReportDetail> => {
  const response = await apiClient.get(`/mbcb/reports/${reportId}`);
  return response.data.data;
};

export const createMbcbReport = async (payload: MbcbReportPayload): Promise<MbcbReportDetail> => {
  const response = await apiClient.post('/mbcb/reports', payload);
  return response.data.data;
};

export const updateMbcbReport = async (
  reportId: number,
  payload: MbcbReportPayload,
): Promise<MbcbReportDetail> => {
  const response = await apiClient.put(`/mbcb/reports/${reportId}`, payload);
  return response.data.data;
};

export const submitMbcbReport = async (reportId: number): Promise<MbcbReportDetail> => {
  const response = await apiClient.post(`/mbcb/reports/${reportId}/submit`);
  return response.data.data;
};

export const approveMbcbReport = async (
  reportId: number,
  notes?: string,
): Promise<MbcbReportDetail> => {
  const response = await apiClient.post(`/mbcb/reports/${reportId}/approve`, { notes });
  return response.data.data;
};

export const requestMbcbRevision = async (
  reportId: number,
  notes: string,
): Promise<MbcbReportDetail> => {
  const response = await apiClient.post(`/mbcb/reports/${reportId}/request-revision`, { notes });
  return response.data.data;
};

export interface MbcbReportReminderResult {
  reportId: number;
  push: {
    success: boolean;
    notificationId?: number;
    totalRecipients: number;
    successfulSends: number;
    failedSends: number;
    mock?: boolean;
    message?: string;
  };
  emailSent: boolean;
}

export const sendMbcbReportReminder = async (reportId: number): Promise<MbcbReportReminderResult> => {
  const response = await apiClient.post(`/mbcb/reports/${reportId}/remind`);
  return response.data.data;
};

/**
 * Planter summary embedded on hub-scoped MBCB report rows (raw attributes from
 * `GET /api/mbcb/reports`, not the self-serve serializer).
 */
export interface HubMbcbReportPlanter {
  id: number;
  name: string;
  spouseName?: string | null;
  churchName?: string | null;
  city?: string | null;
  state?: string | null;
  email?: string | null;
  /** Non-null when the planter has a linked user (required before reminders can be sent). */
  userId?: number | null;
  assignedHubLeader?: number | null;
  pathKind?: 'growth' | 'planting' | null;
  plantingStep?: 'training' | 'funding' | 'post_funding' | null;
  fundingEndDate?: string | null;
  journeyEndedAt?: string | null;
}

export interface HubMbcbReportListItem {
  id: number;
  planterId: number;
  reportMonth: number;
  reportYear: number;
  status: string;
  submittedAt?: string | null;
  reviewedAt?: string | null;
  reviewNotes?: string | null;
  source?: string | null;
  createdAt?: string;
  updatedAt?: string;
  planter?: HubMbcbReportPlanter | null;
}

export interface HubMbcbReportsQuery {
  year?: number;
  month?: number;
  status?: string;
  search?: string;
  planterId?: number;
  page?: number;
  limit?: number;
}

/**
 * Hub-scoped MBCB reports list. The backend automatically narrows hub leaders to
 * planters where `assignedHubLeader === me`; staff/admin/mbcb see all.
 */
export const getHubMbcbReports = async (
  params: HubMbcbReportsQuery = {},
): Promise<PaginatedResponse<HubMbcbReportListItem[]>> => {
  const response = await apiClient.get('/mbcb/reports', { params });
  return response.data;
};

/**
 * Every MBCB report visible to the current hub user, across all periods (paginates
 * until complete). Used to build a per-planter reporting timeline.
 */
export const getAllHubMbcbReports = async (
  params: Omit<HubMbcbReportsQuery, 'page' | 'limit'> = {},
): Promise<HubMbcbReportListItem[]> => {
  const all: HubMbcbReportListItem[] = [];
  let page = 1;
  const limit = 200;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const res = await getHubMbcbReports({ ...params, page, limit });
    all.push(...res.data);
    if (!res.pagination || page >= res.pagination.pages) break;
    page += 1;
  }
  return all;
};

/**
 * All active planters with raw `planting_step = funding`, optionally scoped to a single
 * hub leader (mirrors the backend report scoping for hub-leader-only users). Paginates
 * until the full set is collected.
 */
export const getFundingPlantersForHub = async (
  assignedHubLeader?: number | null,
): Promise<Planter[]> => {
  const all: Planter[] = [];
  let page = 1;
  const limit = 100;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const res = await getPlanters({
      effectivePlantingStep: 'funding',
      journeyEnded: 'false',
      assignedHubLeader: assignedHubLeader != null ? String(assignedHubLeader) : undefined,
      page,
      limit,
    });
    all.push(...res.data);
    if (page >= res.pagination.pages) break;
    page += 1;
  }
  return all;
};
